import React, { useState, useEffect } from "react";
import {
  FaShieldAlt,
  FaArrowLeft,
  FaKey,
  FaExclamationTriangle,
  FaClock,
  FaQuestionCircle,
} from "react-icons/fa";
import {
  verify2FAToken,
  verifyBackupCode,
  get2FASecret,
  getRemainingBackupCodes,
} from "../utils/twoFactorAuth";
import { logAdminActivity } from "../utils/adminSecurity";
import "./TwoFactorVerification.css";

const TwoFactorVerification = ({
  userEmail,
  userUid,
  onVerificationSuccess,
  onCancel,
}) => {
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTime, setLockoutTime] = useState(0);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState("");
  const [remainingBackupCodes, setRemainingBackupCodes] = useState(0);

  // Check remaining backup codes on mount
  useEffect(() => {
    const loadBackupCodes = async () => {
      const remaining = await getRemainingBackupCodes(userUid);
      setRemainingBackupCodes(remaining);
    };
    loadBackupCodes();
  }, [userUid]);

  // Handle lockout timer
  useEffect(() => {
    let timer;
    if (isLocked && lockoutTime > 0) {
      timer = setInterval(() => {
        setLockoutTime((prev) => {
          if (prev <= 1) {
            setIsLocked(false);
            setAttempts(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isLocked, lockoutTime]);

  // Handle verification code input
  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only digits
    if (value.length <= 6) {
      setVerificationCode(value);
      setError("");
    }
  };

  // Handle backup code input
  const handleBackupCodeChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (value.length <= 8) {
      setBackupCode(value);
      setError("");
    }
  };

  // Verify 2FA token
  const handleVerify2FA = async () => {
    if (verificationCode.length !== 6) {
      setError("Lütfen 6 haneli kodu girin.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const secret = await get2FASecret(userUid);
      if (!secret) {
        setError("2FA ayarları bulunamadı. Lütfen yöneticiye başvurun.");
        setIsLoading(false);
        return;
      }

      const isValid = verify2FAToken(verificationCode, secret);

      if (isValid) {
        logAdminActivity("admin_login", {
          email: userEmail,
          timestamp: new Date().toISOString(),
          uid: userUid,
          method: "2fa_authenticator",
        });

        onVerificationSuccess();
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);

        logAdminActivity("2fa_login_failed", {
          userEmail,
          timestamp: new Date().toISOString(),
          method: "authenticator",
          attempts: newAttempts,
        });

        if (newAttempts >= 3) {
          setIsLocked(true);
          setLockoutTime(300); // 5 minutes
          setError("Çok fazla başarısız deneme. 5 dakika bekleyin.");
        } else {
          setError(`Geçersiz kod. ${3 - newAttempts} deneme hakkınız kaldı.`);
        }

        setVerificationCode("");
      }
    } catch (error) {
      console.error("2FA verification error:", error);
      setError("Doğrulama sırasında hata oluştu.");
    }

    setIsLoading(false);
  };

  // Verify backup code
  const handleVerifyBackupCode = async () => {
    if (backupCode.length !== 8) {
      setError("Yedek kod 8 karakter olmalıdır.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const isValid = await verifyBackupCode(backupCode, userUid, userEmail);

      if (isValid) {
        logAdminActivity("admin_login", {
          email: userEmail,
          timestamp: new Date().toISOString(),
          uid: userUid,
          method: "2fa_backup_code",
        });

        // Update remaining backup codes
        const remaining = await getRemainingBackupCodes(userUid);
        setRemainingBackupCodes(remaining);

        onVerificationSuccess();
      } else {
        logAdminActivity("2fa_login_failed", {
          userEmail,
          timestamp: new Date().toISOString(),
          method: "backup_code",
        });

        setError("Geçersiz veya kullanılmış yedek kod.");
        setBackupCode("");
      }
    } catch (error) {
      console.error("Backup code verification error:", error);
      setError("Yedek kod doğrulaması sırasında hata oluştu.");
    }

    setIsLoading(false);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (isLocked) return;

    if (useBackupCode) {
      handleVerifyBackupCode();
    } else {
      handleVerify2FA();
    }
  };

  return (
    <div className="two-factor-verification">
      <div className="verification-container">
        <div className="verification-header">
          <button className="back-button" onClick={onCancel}>
            <FaArrowLeft />
            Geri Dön
          </button>
        </div>

        <div className="verification-content">
          <div className="verification-icon">
            <FaShieldAlt />
          </div>

          <h2>İki Faktörlü Doğrulama</h2>
          <p className="verification-subtitle">
            {useBackupCode
              ? "Yedek kodunuzu girin"
              : "Authenticator uygulamanızdaki kodu girin"}
          </p>

          {/* User Info */}
          <div className="user-info">
            <span className="user-email">{userEmail}</span>
          </div>

          {/* Lockout Warning */}
          {isLocked && (
            <div className="lockout-warning">
              <FaExclamationTriangle className="lockout-icon" />
              <div className="lockout-content">
                <h4>Hesap Geçici Olarak Kilitli</h4>
                <p>
                  Çok fazla başarısız deneme nedeniyle geçici olarak
                  kilitlenmiştir.
                </p>
                <p className="lockout-timer">
                  Kalan süre:{" "}
                  <strong>
                    {Math.floor(lockoutTime / 60)}:
                    {(lockoutTime % 60).toString().padStart(2, "0")}
                  </strong>
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="verification-form">
            {!useBackupCode ? (
              // Authenticator Code Input
              <div className="code-input-section">
                <label htmlFor="verification-code">Doğrulama Kodu</label>
                <input
                  type="text"
                  id="verification-code"
                  value={verificationCode}
                  onChange={handleCodeChange}
                  placeholder="000000"
                  className="code-input"
                  maxLength={6}
                  autoComplete="off"
                  disabled={isLocked}
                />
                <div className="code-info">
                  <FaClock className="info-icon" />
                  <span>Kod her 30 saniyede bir yenilenir</span>
                </div>
              </div>
            ) : (
              // Backup Code Input
              <div className="code-input-section">
                <label htmlFor="backup-code">Yedek Kod</label>
                <input
                  type="text"
                  id="backup-code"
                  value={backupCode}
                  onChange={handleBackupCodeChange}
                  placeholder="XXXXXXXX"
                  className="backup-input"
                  maxLength={8}
                  autoComplete="off"
                  disabled={isLocked}
                />
                <div className="backup-info">
                  <FaKey className="info-icon" />
                  <span>Kalan yedek kod: {remainingBackupCodes}</span>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="error-message">
                <FaExclamationTriangle />
                <span>{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className={`verify-button ${isLocked ? "locked" : ""}`}
              disabled={
                isLocked ||
                isLoading ||
                (!useBackupCode && verificationCode.length !== 6) ||
                (useBackupCode && backupCode.length !== 8)
              }
            >
              <FaShieldAlt />
              {isLocked
                ? `Kilitli (${Math.floor(lockoutTime / 60)}:${(lockoutTime % 60)
                    .toString()
                    .padStart(2, "0")})`
                : isLoading
                ? "Doğrulanıyor..."
                : "Doğrula"}
            </button>
          </form>

          {/* Switch between methods */}
          <div className="verification-options">
            {!useBackupCode ? (
              <button
                className="option-button"
                onClick={() => setUseBackupCode(true)}
                disabled={isLocked || remainingBackupCodes === 0}
              >
                <FaKey />
                Yedek Kod Kullan
                {remainingBackupCodes === 0 && " (Yok)"}
              </button>
            ) : (
              <button
                className="option-button"
                onClick={() => setUseBackupCode(false)}
                disabled={isLocked}
              >
                <FaShieldAlt />
                Authenticator Kodu Kullan
              </button>
            )}
          </div>

          {/* Help Section */}
          <div className="help-section">
            <div className="help-item">
              <FaQuestionCircle className="help-icon" />
              <div>
                <h4>Yardıma mı ihtiyacınız var?</h4>
                <p>
                  {useBackupCode
                    ? "Yedek kodlarınızı 2FA kurulumu sırasında indirmiştiniz."
                    : "Google Authenticator, Authy veya benzer uygulamalardaki 6 haneli kodu girin."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorVerification;
